import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ApprovalDetailField } from '../../components/ApprovalDetailField';
import { RequestCancelActions } from '../../components/RequestCancelActions';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  getLeaveModuleCapabilities,
  LEAVE_ROUTE_CAPABILITIES,
} from '../../config/leaveCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  approveLeaveApplication,
  cancelLeaveApplication,
  getLeaveApplicationDetails,
  rejectLeaveApplication,
} from '../../services/leaveService';
import { getWorkflowDetails } from '../../services/workflowService';
import type { LeaveApplicationDetails } from '../../types/leave';
import type { WorkflowDetails } from '../../types/workflow';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { formatAppliedOn, formatLeaveDate, formatLeaveDuration } from '../../utils/dateTime';
import {
  formatLeaveSessionLabel,
  leavePortionLabelForDays,
} from '../../utils/leaveSession';
import { workflowErrorMessage } from '../../utils/workflowErrors';
import { confirmAction } from '../../utils/confirmAction';
import { leaveErrorMessage } from '../../utils/leaveErrors';
import { canShowApprovedRequestCancel } from '../../utils/requestCancelWindow';
import {
  isPendingWorkflowActionStatus,
  isRejectedWorkflowStatus,
  isCancelledWorkflowStatus,
} from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveApproval'>;

function text(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function numberValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function isLeaveWorkflow(code: string | null | undefined): boolean {
  const normalized = (code || '').toUpperCase();
  return (
    normalized === 'LEAVE_REQUEST' ||
    normalized === 'LEAVE' ||
    normalized === 'LEAVE_APPLICATION'
  );
}

function mergeLeaveApplicationDetails(
  workflow: WorkflowDetails,
  leave: LeaveApplicationDetails,
): WorkflowDetails {
  const leaveOwner = leave.employee;
  const leaveOwnerName = leaveOwner?.displayName?.trim() || null;
  const leaveOwnerNumber = leaveOwner?.employeeNumber?.trim() || null;
  const leaveOwnerDesignation = leaveOwner?.designationName?.trim() || null;
  return {
    ...workflow,
    employee: {
      employeeId: leaveOwner?.employeeId ?? workflow.employee.employeeId,
      employeeNumber:
        leaveOwnerNumber || workflow.employee.employeeNumber,
      displayName: leaveOwnerName || workflow.employee.displayName,
      departmentName: workflow.employee.departmentName,
      designationName:
        leaveOwnerDesignation || workflow.employee.designationName,
    },
    requestDetails: {
      ...(workflow.requestDetails ?? {}),
      leaveTypeName:
        leave.leaveType.leaveTypeName ??
        workflow.requestDetails?.leaveTypeName,
      leaveFromDate:
        leave.leaveFromDate ?? workflow.requestDetails?.leaveFromDate,
      leaveToDate: leave.leaveToDate ?? workflow.requestDetails?.leaveToDate,
      totalDays: leave.totalDays ?? workflow.requestDetails?.totalDays,
      sessionType: leave.sessionType ?? workflow.requestDetails?.sessionType,
      sessionTypeName:
        leave.sessionTypeName ?? workflow.requestDetails?.sessionTypeName,
      reason: leave.reason ?? workflow.requestDetails?.reason,
      handoverNotes:
        leave.handoverNotes ?? workflow.requestDetails?.handoverNotes,
    },
    rejectReason: leave.rejectReason ?? workflow.rejectReason,
    cancellationReason:
      leave.cancellationReason ?? workflow.cancellationReason,
    canCancel: leave.canCancel,
  };
}

function Field({
  label,
  value,
  palette,
  link = false,
  expandable = false,
}: {
  label: string;
  value: string;
  palette: ColorPalette;
  link?: boolean;
  expandable?: boolean;
}) {
  return (
    <ApprovalDetailField
      label={label}
      value={value}
      palette={palette}
      link={link}
      expandable={expandable}
    />
  );
}

export function LeaveApprovalScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getLeaveModuleCapabilities(auth.permissions);
  const workflowInstanceId = route.params.workflowInstanceId;

  const [details, setDetails] = useState<WorkflowDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const canAct =
    LEAVE_ROUTE_CAPABILITIES.approveAction &&
    LEAVE_ROUTE_CAPABILITIES.rejectAction &&
    isPendingWorkflowActionStatus(details?.workflowStatusCode);
  const isRejected = isRejectedWorkflowStatus(details?.workflowStatusCode);
  const isCancelled = isCancelledWorkflowStatus(details?.workflowStatusCode);
  const leaveApplicationId = details?.requestReferenceId ?? null;

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canAccessLeaveApproval) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!Number.isInteger(workflowInstanceId) || workflowInstanceId <= 0) {
        setDetails(null);
        setError('Leave request was not found.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const next = await getWorkflowDetails(workflowInstanceId);
        if (next.requestTypeCode && !isLeaveWorkflow(next.requestTypeCode)) {
          if (!isRefresh) setDetails(null);
          setError('This approval is not a leave request.');
          return;
        }

        let resolved = next;
        if (next.requestReferenceId != null) {
          try {
            const leave = await getLeaveApplicationDetails(
              next.requestReferenceId,
            );
            resolved = mergeLeaveApplicationDetails(next, leave);
          } catch {
            resolved = next;
          }
        }
        setDetails(resolved);
        setError(null);
      } catch (caught) {
        if (!isRefresh) setDetails(null);
        setError(
          workflowErrorMessage(
            caught,
            'Unable to load leave approval details.',
          ),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canAccessLeaveApproval, workflowInstanceId],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const handleApprove = () => {
    if (!canAct || acting || leaveApplicationId == null) return;
    confirmAction({
      title: 'Approve leave',
      message: 'Approve this leave request?',
      confirmText: 'Approve',
      onConfirm: () => {
        setActing(true);
        setActionError(null);
        return approveLeaveApplication(leaveApplicationId)
          .then(() => navigation.goBack())
          .catch(caught => {
            setActionError(
              leaveErrorMessage(caught, 'Unable to approve leave request.'),
            );
          })
          .finally(() => setActing(false));
      },
    });
  };

  const handleReject = async () => {
    if (!canAct || acting || leaveApplicationId == null) return;
    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      setActionError('Rejection reason is required.');
      return;
    }
    setActing(true);
    setActionError(null);
    try {
      await rejectLeaveApplication(leaveApplicationId, trimmedReason);
      navigation.goBack();
    } catch (caught) {
      setActionError(
        leaveErrorMessage(caught, 'Unable to reject leave request.'),
      );
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (acting || leaveApplicationId == null) return;
    setActing(true);
    setActionError(null);
    try {
      await cancelLeaveApplication(leaveApplicationId, reason);
      navigation.goBack();
    } catch (caught) {
      setActionError(
        leaveErrorMessage(caught, 'Unable to cancel leave request.'),
      );
    } finally {
      setActing(false);
    }
  };

  const employeeName =
    details?.employee.displayName ||
    details?.employee.employeeNumber ||
    'Employee unavailable';
  const requestDetails = details?.requestDetails ?? null;
  const leaveType = formatDisplayLabel(
    text(requestDetails?.leaveTypeName) ||
      text(requestDetails?.leaveName) ||
      details?.requestTypeLabel,
    'Not available',
  );
  const fromDate = formatLeaveDate(
    text(
      requestDetails?.leaveFromDate ??
        requestDetails?.fromDate ??
        requestDetails?.startDate,
    ),
  );
  const toDate = formatLeaveDate(
    text(
      requestDetails?.leaveToDate ??
        requestDetails?.toDate ??
        requestDetails?.endDate,
    ),
  );
  const requestedDays = numberValue(
    requestDetails?.totalDays ??
      requestDetails?.leaveDays ??
      requestDetails?.numberOfDays,
  );
  const totalDays = formatLeaveDuration(requestedDays);
  const session = formatLeaveSessionLabel(
    text(requestDetails?.sessionType ?? requestDetails?.session),
    [],
    text(requestDetails?.sessionTypeName ?? requestDetails?.sessionName),
  );
  const reason =
    text(requestDetails?.reason) ||
    text(requestDetails?.leaveReason) ||
    'Not available';
  const handoverNotes =
    text(requestDetails?.handoverNotes) ||
    text(requestDetails?.handover_notes) ||
    'Not available';
  const appliedOn = formatAppliedOn(details?.submittedAt ?? null);
  const storedRejectReason =
    details?.rejectReason ||
    text(requestDetails?.rejectReason) ||
    text(requestDetails?.rejectionReason);
  const storedCancellationReason =
    details?.cancellationReason ||
    text(requestDetails?.cancellationReason) ||
    text(requestDetails?.cancelReason);
  const attachment = text(
    requestDetails?.attachmentName ?? requestDetails?.attachment,
  );

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
        textAlign: 'center' as const,
      },
      actionError: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.LEAVE_APPROVE}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Leave request detail"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading leave approval" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Leave request detail"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                load(true).catch(() => undefined);
              }}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader title={employeeName} onBack={navigation.goBack} />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.profileRow}>
                <ProfileAvatar name={employeeName} size={56} />
                <View style={styles.profileText}>
                  <Text style={themed.name}>{employeeName}</Text>
                  {details.employee.employeeNumber ? (
                    <Text style={themed.meta}>
                      {details.employee.employeeNumber}
                    </Text>
                  ) : null}
                  {details.employee.designationName ? (
                    <Text style={themed.meta}>
                      {details.employee.designationName}
                    </Text>
                  ) : null}
                </View>
                <LeaveStatusBadge
                  label={details.workflowStatusLabel}
                  statusCode={details.workflowStatusCode}
                />
              </View>

              <Field label="Leave Type" value={leaveType} palette={palette} />
              <Field label="From Date" value={fromDate} palette={palette} />
              <Field label="To Date" value={toDate} palette={palette} />
              <Field label="Total Days" value={totalDays} palette={palette} />
              <Field
                label={leavePortionLabelForDays(requestedDays)}
                value={session}
                palette={palette}
              />
              <Field label="Reason" value={reason} palette={palette} />
              <Field
                label="Handover Notes"
                value={handoverNotes}
                palette={palette}
                expandable
              />
              <Field label="Applied On" value={appliedOn} palette={palette} />
              {isRejected ? (
                <Field
                  label="Reject Reason"
                  value={storedRejectReason || 'Not available'}
                  palette={palette}
                  expandable
                />
              ) : null}
              {isCancelled ? (
                <Field
                  label="Cancellation Reason"
                  value={storedCancellationReason || 'Not available'}
                  palette={palette}
                  expandable
                />
              ) : null}
              {attachment ? (
                <Field
                  label="Attachments"
                  value={attachment}
                  palette={palette}
                  link
                />
              ) : null}
            </AppCard>

            {isPendingWorkflowActionStatus(details.workflowStatusCode) &&
            !canAct ? (
              <Text style={themed.warning}>
                Leave approve and reject actions are not available in this app
                yet.
              </Text>
            ) : null}

            {actionError ? (
              <Text accessibilityRole="alert" style={themed.actionError}>
                {actionError}
              </Text>
            ) : null}

            {canAct && rejectMode ? (
              <View style={styles.rejectBlock}>
                <AppInput
                  label="Rejection reason"
                  value={rejectReason}
                  onChangeText={value => {
                    setRejectReason(value);
                    setActionError(null);
                  }}
                  placeholder="Enter reason for rejection"
                />
                <AppButton
                  title="Confirm Reject"
                  variant="danger"
                  onPress={handleReject}
                  loading={acting}
                  disabled={acting}
                />
                <AppButton
                  title="Cancel"
                  variant="text"
                  onPress={() => {
                    setRejectMode(false);
                    setRejectReason('');
                    setActionError(null);
                  }}
                  disabled={acting}
                />
              </View>
            ) : canAct ? (
              <View style={styles.actions}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Reject"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={() => {
                    setRejectMode(true);
                    setActionError(null);
                  }}
                  style={[
                    styles.actionButton,
                    styles.rejectButton,
                    {
                      borderColor: palette.error,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.rejectLabel, { color: palette.error }]}>
                    Reject
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Approve"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={handleApprove}
                  style={[
                    styles.actionButton,
                    {
                      backgroundColor: palette.success,
                      borderColor: palette.success,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.approveLabel, { color: palette.onSuccess }]}>
                    Approve
                  </Text>
                </Pressable>
              </View>
            ) : null}

            <RequestCancelActions
              visible={canShowApprovedRequestCancel({
                capabilityCanCancel: capabilities.canCancel,
                canAct,
                statusCode: details.workflowStatusCode,
                fromDate: text(
                  requestDetails?.leaveFromDate ??
                    requestDetails?.fromDate ??
                    requestDetails?.startDate,
                ),
                toDate: text(
                  requestDetails?.leaveToDate ??
                    requestDetails?.toDate ??
                    requestDetails?.endDate,
                ),
              })}
              palette={palette}
              startTitle="Cancel Leave"
              confirmTitle="Confirm Cancel"
              keepTitle="Keep Leave"
              busy={acting}
              error={actionError}
              onConfirm={handleCancel}
            />
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  profileRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
    minWidth: 0,
  },
  profileText: { flex: 1, gap: 2, minWidth: 0 },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  rejectBlock: {
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  actionButton: {
    flexGrow: 1,
    flexBasis: 140,
    minWidth: 140,
    maxWidth: '100%',
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectButton: {
    backgroundColor: 'transparent',
  },
  rejectLabel: {
    ...typography.button,
    fontWeight: '700',
  },
  approveLabel: {
    ...typography.button,
    fontWeight: '700',
  },
});
